<template>
  <v-card flat class="mt-1">
    <v-slide-y-transition>
      <v-card-title v-if="showGridFilter">
        <v-layout row wrap>
          <v-flex sm6>
            <v-layout>
              <v-flex sm3>
                <v-combobox v-model="selectedServices" :label="messages.label.SERVICE" multiple tags
                  :items="servicesList"></v-combobox>
              </v-flex>
              <v-flex sm3 class="ml-2">
                <v-combobox v-model="selectedStatus" :label="messages.label.STATUS" multiple tags :items="statusList">
                </v-combobox>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex sm6 class="text-xs-right">
            <v-menu bottom left :close-on-content-click="false" transition="slide-y-transition">
              <v-btn slot="activator" flat>
                <v-icon>settings</v-icon>
                <span class="ml-1">Customize Columns</span>
              </v-btn>
              <v-list>
                <template v-for="(header, index) in headers">
                  <v-list-tile :key="index" v-show="header.text.length > 0">
                    <v-list-tile-action>
                      <v-checkbox color="primary" v-model="header.selected"></v-checkbox>
                    </v-list-tile-action>
                    <v-list-tile-content>
                      <v-list-tile-title>{{ header.text }}</v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </template>
              </v-list>
            </v-menu>
          </v-flex>
        </v-layout>
      </v-card-title>
    </v-slide-y-transition>
    <v-card-text class="py-1">
      <v-data-table v-model="selectedAssets" :headers="filteredHeaders" :items="filteredSearchResult"
        :pagination.sync="pagination" select-all item-key="id" :loading="assetsLoading"
        :rows-per-page-items="rowsPerPagesArray">
        <template slot="headers" slot-scope="props">
          <tr>
            <th>
              <v-checkbox :input-value="props.all" :indeterminate="props.indeterminate" primary hide-details
                @click.native="toggleAll()" color="primary"></v-checkbox>
            </th>
            <th v-for="header in props.headers" :key="header.text"
              :class="[header.sortable ? 'column sortable' : '', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '', { 'colorFiltered': isFilterApplied(header.value) }]"
              @click="changeSort(header.value, header.sortable)">
              <span v-if="isFilterApplied(header.value)">FILTER ON</span><br>
              <v-icon v-if="header.sortable">arrow_upward</v-icon>
              {{ header.text }}
            </th>
          </tr>
        </template>
        <!-- Items -->
        <template slot="items" slot-scope="props">
          <tr :active="props.selected" @click="props.selected = !props.selected">
            <td>
              <v-checkbox v-model="props.selected" primary hide-details color="primary"></v-checkbox>
            </td>
            <td v-if="showColumn('house_id')" class="text-xs-right">{{ props.item.house_id }}</td>
            <td v-if="showColumn('title')" class="text-xs-right">{{ props.item.title }}</td>
            <td v-if="showColumn('content')" class="text-xs-right">{{ props.item.content }}</td>
            <td v-if="showColumn('date')" class="text-xs-right">{{ props.item.date }}</td>
            <td v-if="showColumn('alt')" class="text-xs-right">{{ props.item.alt }}</td>
            <td v-if="showColumn('upload')" :class="[{ 'highlightFiltered': isFilterApplied('upload') }, 'text-xs-right']">
              {{ props.item.upload }}</td>
            <td v-if="showColumn('distribute')"
              :class="[{ 'highlightFiltered': isFilterApplied('distribute') }, 'text-xs-right']">{{ props.item.distribute }}
            </td>
            <td v-if="showColumn('VDS')" :class="[{ 'highlightFiltered': isFilterApplied('VDS') }, 'text-xs-right']">{{
                props.item.VDS
            }}</td>
            <td v-if="showColumn('CCP')" :class="[{ 'highlightFiltered': isFilterApplied('CCP') }, 'text-xs-right']">{{
                props.item.CCP
            }}</td>
            <td v-if="showColumn('CCDN')" :class="[{ 'highlightFiltered': isFilterApplied('CCDN') }, 'text-xs-right']">{{
                props.item.CCDN
            }}</td>
            <td v-if="showColumn('MDH')" :class="[{ 'highlightFiltered': isFilterApplied('MDH') }, 'text-xs-right']">{{
                props.item.MDH
            }}</td>
            <td>
              <v-menu bottom right :disabled="assetsLoading">
                <v-btn icon slot="activator">
                  <v-icon>more_vert</v-icon>
                </v-btn>
                <v-list>
                  <v-list-tile>
                    <v-list-tile-title>{{ messages.label.VIEW_XML }}</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile>
                    <v-list-tile-title>{{ messages.label.COMPLETE }}</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile>
                    <v-list-tile-title>{{ messages.label.RETRY }}</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile>
                    <v-list-tile-title>{{ messages.label.FAIL }}</v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile>
                    <v-list-tile-title>{{ messages.label.EDIT_ALT_CODE }}</v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
        <!-- No Data -->
        <template slot="no-data">
          <v-alert :value="true" :color="initiatedSearch ? 'error' : 'info'"
            :class="{ 'black--text': !initiatedSearch, 'white--text': initiatedSearch }"
            :icon="!initiatedSearch ? 'info' : 'warning'">
            <span v-if="!initiatedSearch">
              {{ messages.info.SELECT_SEARCH_CRITERIA }}
            </span>
            <span v-else>
              {{ messages.info.NO_SEARCH_RESULTS }}
            </span>
          </v-alert>
        </template>
        <!-- Page Text -->
        <template slot="pageText" slot-scope="props">
          {{ props.pageStart }} - {{ props.pageStop }} of {{ props.itemsLength }}
        </template>
      </v-data-table>
      <v-snackbar :timeout="snackbar.timeout" :top="true" color="light-green black--text" v-model="snackbar.show">
        {{ snackbar.message }}
        <v-btn flat color="primary" @click.native="snackbar.show = false">Ok</v-btn>
      </v-snackbar>
    </v-card-text>

  </v-card>
</template>

<script>
import localforage from "localforage"
import AssetStatusService from "@/AssetStatus/AssetStatusService"
import AppService from "@/AppService"
import * as appDataService from "@/AppDataService"
import messages from "@/AssetStatus/messages.json"

export default {
  name: "AssetStatusSearchResult",
  components: {},
  data() {
    return {
      search: "",
      assetsLoading: false,
      initiatedSearch: false,
      pagination: {
        sortBy: "house_id",
        page: 1,
        rowsPerPage: 25,
        descending: false
      },
      snackbar: {
        show: false,
        message: "",
        timeout: 6000
      },
      rowsPerPagesArray: [25, 50, 75, 100],
      headers: [],
      searchResult: this.getSearchResult(),
      mpxToken: "",
      searchObject: {},
      selectedAssets: [],
      selectedServices: [],
      selectedStatus: [],
      servicesList: ["upload", "distribute", "VDS", "CCP", "CCDN", "MDH"],
      statusList: ["In Progress", "Complete", "Queued"]
    }
  },
  watch: {
    /**
     * Watch handler for headers object
     */
    headers: {
      handler(newVal) {
        AppService.setAssetStatusTableHeaders(newVal)
      },
      deep: true
    }
  },
  computed: {
    showGridFilter() {
      return this.searchResult.length > 0
    },
    filteredHeaders() {
      return this.headers.filter((h) => h.selected)
    },
    filteredSearchResult() {
      let filteredArrayByColumns = this.searchResult.map((item) => {
        let filtered = Object.assign({}, item)
        this.headers.forEach((header) => {
          if (!header.selected) delete filtered[header.value]
        })
        return filtered
      })
      const displayedKeys = this.selectedServices
      const dispalyedStatus = this.selectedStatus
      let filteredSearchResult = filteredArrayByColumns

      if (this.selectedStatus.length > 0 && this.selectedServices.length > 0) {
        filteredSearchResult = filteredArrayByColumns.filter((obj) => {
          let filteredObj = this.$lodash.pick(obj, displayedKeys)
          return Object.keys(filteredObj).some(function (key) {
            if (
              filteredObj[key] !== null &&
              this.$lodash.indexOf(dispalyedStatus, filteredObj[key]) > -1
            ) {
              return true
            }
          })
        })
      }
      return filteredSearchResult
    }
  },
  methods: {
    showColumn(col) {
      if (!this.$lodash.isEmpty(this.headers)) {
        return this.headers.find((h) => h.value === col).selected
      }
    },
    toggleAll() {
      if (this.selectedAssets.length) {
        this.selectedAssets = []
      } else {
        this.selectedAssets = this.searchResult.slice()
      }
    },
    changeSort(columnName, columnSortable) {
      if (this.pagination.sortBy === columnName && columnSortable) {
        this.pagination.descending = !this.pagination.descending
      } else if (columnSortable) {
        this.pagination.sortBy = columnName
        this.pagination.descending = false
      }
    },
    isFilterApplied(value) {
      let rtnVal = false
      if (this.$lodash.indexOf(this.selectedServices, value) > -1) {
        rtnVal = true
      }
      return rtnVal
    },
    /**
     * getTableHeaders - Construct array of objects to show table headers
     *
     * @return {array}
     */
    getTableHeaders() {
      let headers = [
        {
          text: messages.label.TH_HOUSE_ID,
          align: "left",
          value: "house_id",
          selected: true
        },
        {
          text: messages.label.TH_TITLE,
          align: "left",
          value: "title",
          selected: true
        },
        {
          text: messages.label.TH_CONTENT,
          align: "left",
          value: "content",
          selected: true
        },
        {
          text: messages.label.TH_CREATED_DATE,
          align: "left",
          value: "date",
          selected: true
        },
        {
          text: messages.label.TH_ALT_CODE,
          align: "left",
          value: "alt",
          selected: true
        },
        {
          text: messages.label.TH_UPLOAD,
          align: "left",
          value: "upload",
          selected: true
        },
        {
          text: messages.label.TH_DISTRIBUTE,
          align: "left",
          value: "distribute",
          selected: true
        },
        {
          text: messages.label.TH_VDS,
          align: "left",
          value: "VDS",
          selected: true
        },
        {
          text: messages.label.TH_CCP,
          align: "left",
          value: "CCP",
          selected: true
        },
        {
          text: messages.label.TH_CCDN,
          align: "left",
          value: "CCDN",
          selected: true
        },
        {
          text: messages.label.TH_MDH,
          align: "left",
          value: "MDH",
          selected: true
        },
        {
          text: "",
          sortable: false,
          selected: true
        }
      ]
      return headers
    },
    getSearchResult() {
      let searchResult = [
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test1",
          date: "08/20/2018",
          alt: "PCKG042",
          upload: "Complete",
          distribute: "Complete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "1"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test2",
          date: "08/21/2018",
          alt: "PCKG042",
          upload: "Complete",
          distribute: "Complete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "2"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test3",
          date: "08/22/2018",
          alt: "PCKG042",
          upload: "",
          distribute: "",
          VDS: "Complete",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "3"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test4",
          date: "08/23/2018",
          alt: "PCKG042",
          upload: "",
          distribute: "ete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "4"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test5",
          date: "08/24/2018",
          alt: "PCKG042",
          upload: "",
          distribute: "lete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "5"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test6",
          date: "08/25/2018",
          alt: "PCKG042",
          upload: "",
          distribute: "lete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "6"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test7",
          date: "08/26/2018",
          alt: "PCKG042",
          upload: "",
          distribute: "ete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "7"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test8",
          date: "08/27/2018",
          alt: "PCKG042",
          upload: "Complete",
          distribute: "Complete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "8"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test9",
          date: "08/28/2018",
          alt: "PCKG042",
          upload: "Complete",
          distribute: "Complete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "9"
        },
        {
          house_id: "HAVOC_25APR2018_PCKG04",
          title: "PanicAtDisco:Overtu",
          content: "test10",
          date: "08/29/2018",
          alt: "PCKG042",
          upload: "Complete",
          distribute: "Complete",
          VDS: "In Progress",
          CCP: "Complete",
          CCDN: "Queued",
          MDH: "Queued",
          id: "10"
        }
      ]
      return searchResult
    },
    /**
     * getAssetStatusTableHeaders - Retrieve customize columns set by users
     * from local storage
     */
    getAssetStatusTableHeaders() {
      return localforage
        .getItem("assetStatusTableHeaders")
        .then((value) => {
          return value
        })
        .catch((error) => {
          console.log(error)
          return {}
        })
    },
    /**
     * setAssetStatusTableHeaders - Set customize table columns in to local storage
     * @param  {type} headers table headers array
     */
    setAssetStatusTableHeaders(headers) {
      localforage.setItem("assetStatusTableHeaders", headers)
    },
    searchAssets() {
      this.initiatedSearch = true
      // Add futher functionality for getting assets
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    //  Retrieve mpx token
    appDataService.getMPXUserInfo().then((userInfo) => {
      this.mpxToken = userInfo.token
    })
    //  Retrieve saved headers
    this.getAssetStatusTableHeaders().then((savedHeaders) => {
      if (savedHeaders !== null && savedHeaders.length > 0) {
        this.headers = savedHeaders
      } else {
        this.headers = this.getTableHeaders()
      }
    })
    /**
     * Events
     */
    AssetStatusService.$on("event:AssetStatusSerach", () => {
      this.searchAssets()
    })
  },
  beforeDestroy() {
    AssetStatusService.$off("event:AssetStatusSerach")
    AssetStatusService.$off("event:AssetStatusSerach")
  }
}
</script>

<style scoped>
.highlightFiltered {
  border: 1px solid lightskyblue !important;
}

.colorFiltered {
  color: white;
  background-color: lightblue;
}
</style>



// WEBPACK FOOTER //
// src/AssetStatus/AssetStatusSearchResult.vue